/** List-style pages: department, Shopify collection, search, all collections, help. */
import { useEffect, useState } from "react";
import type { Account, CategoryPage, CategorySummary, DepartmentPage, NavNode, SearchResponse } from "./types";
import { Go, type GoFn } from "./route";
import { deptRoute } from "./nav";
import { get } from "./shared";
import { Pic } from "./img";
import { setSeo } from "./seo";
import { track } from "./track";
import { facetsFromCards, Listing } from "./Listing";

/** Fetches `path` whenever it changes; `null` data with `busy` false means it failed (or 404). */
function useLoad<T>(path: string | null): { data: T | null; busy: boolean } {
  const [state, setState] = useState<{ path: string | null; data: T | null; busy: boolean }>({ path, data: null, busy: path !== null });
  useEffect(() => {
    if (path === null) { setState({ path, data: null, busy: false }); return; }
    const ac = new AbortController();
    setState({ path, data: null, busy: true });
    get<T>(path, ac.signal)
      .then((data) => { if (!ac.signal.aborted) setState({ path, data, busy: false }); })
      .catch(() => { if (!ac.signal.aborted) setState({ path, data: null, busy: false }); });
    return () => ac.abort();
  }, [path]);
  // The first render after `path` changes still holds the previous result; never show it for the new path.
  return state.path === path ? { data: state.data, busy: state.busy } : { data: null, busy: path !== null };
}

function ListSkeleton() {
  return (
    <div className="wrap list-page">
      <div className="list-layout">
        <div className="side" />
        <div className="grid">{Array.from({ length: 8 }, (_, i) => (
          <div key={i}><div className="skel sq" /><div className="skel line1" /><div className="skel line2" /></div>
        ))}</div>
      </div>
    </div>
  );
}

function NotFound({ go, what }: { go: GoFn; what: string }) {
  return (
    <div className="empty">
      <h1>We could not find that {what}</h1>
      <p>It may have moved. Try the department menu or search.</p>
      <Go to={{ name: "home" }} go={go} className="btn btn-quiet">Back to the shop</Go>
    </div>
  );
}

/** Any address the router does not know — the same answer the server gives it (a 404). */
export function NotFoundPage({ go }: { go: GoFn }) {
  useEffect(() => { setSeo("Page not found", null); }, []);
  return <NotFound go={go} what="page" />;
}

export function DepartmentRoute({ id, qs, go, nav, account }: { id: string; qs: string; go: GoFn; nav: NavNode[]; account: Account | null }) {
  const { data, busy } = useLoad<DepartmentPage>(`/shop/department/${encodeURIComponent(id)}`);
  useEffect(() => {
    if (data) setSeo(data.title, `Shop ${data.title} — ${data.productCount} products, delivered in 2–7 working days.`);
  }, [data]);
  if (busy) return <ListSkeleton />;
  if (!data) return <NotFound go={go} what="department" />;
  const crumbs = [{ label: "Home", to: { name: "home" } as const }, ...data.breadcrumbs.map((b, i, a) => (i === a.length - 1 ? { label: b.name } : { label: b.name, to: deptRoute(b) }))];
  return (
    <Listing
      title={data.title}
      crumbs={crumbs}
      facts={data.intro || undefined}
      guides={data.guides}
      products={data.products}
      facets={data.facets}
      priceRange={data.priceRange}
      qs={qs}
      mk={(next) => deptRoute({ id, slug: data.breadcrumbs[data.breadcrumbs.length - 1]?.slug ?? "" }, next)}
      go={go}
      trade={account?.list === "wholesale"}
      tree={nav}
      currentId={id}
      aboveGrid={data.children.length > 0 ? (
        <ul className="subdepts" aria-label="Sub-departments">
          {data.children.map((c) => <li key={c.id}><Go to={deptRoute(c)} go={go}>{c.name} <span className="n">({c.productCount})</span></Go></li>)}
        </ul>
      ) : undefined}
    />
  );
}

/** `/clearance` — every clearance line, on the same Listing layout as a department. */
export function ClearanceRoute({ qs, go, nav, account }: { qs: string; go: GoFn; nav: NavNode[]; account: Account | null }) {
  const { data, busy } = useLoad<CategoryPage>("/shop/clearance");
  useEffect(() => { setSeo("Clearance", "Clearance at Z S MERCH — last units marked down until they are gone."); }, []);
  if (busy) return <ListSkeleton />;
  if (!data) return <NotFound go={go} what="clearance page" />;
  const trade = account?.list === "wholesale";
  return (
    <Listing
      title="Clearance"
      crumbs={[{ label: "Home", to: { name: "home" } }, { label: "Clearance" }]}
      intro={trade ? "Last units at clearance prices — you pay whichever is lower, your wholesale price or the clearance price." : data.description ?? undefined}
      products={data.products}
      facets={data.facets}
      priceRange={data.priceRange}
      qs={qs}
      mk={(next) => ({ name: "clearance", qs: next })}
      go={go}
      trade={trade}
      tree={nav}
      currentId={null}
      emptyHint="Nothing is on clearance right now. Check back soon."
    />
  );
}

/** A Shopify collection (`/c/<handle>`), kept for old links and "All collections". */
export function CollectionRoute({ handle, qs, go, nav, account }: { handle: string; qs: string; go: GoFn; nav: NavNode[]; account: Account | null }) {
  const { data, busy } = useLoad<CategoryPage>(`/shop/category/${encodeURIComponent(handle)}`);
  useEffect(() => { if (data) setSeo(data.title, `Shop ${data.title} at Z S MERCH.`); }, [data]);
  if (busy) return <ListSkeleton />;
  if (!data) return <NotFound go={go} what="collection" />;
  return (
    <Listing
      title={data.title}
      crumbs={[{ label: "Home", to: { name: "home" } }, { label: "All collections", to: { name: "collections" } }, { label: data.title }]}
      intro={data.description ?? undefined}
      facts={data.intro || undefined}
      guides={data.guides}
      banner={data.image}
      products={data.products}
      facets={data.facets}
      priceRange={data.priceRange}
      qs={qs}
      mk={(next) => ({ name: "category", handle, qs: next })}
      go={go}
      trade={account?.list === "wholesale"}
      tree={nav}
      currentId={null}
    />
  );
}

export function SearchRoute({ qs, go, nav, account }: { qs: string; go: GoFn; nav: NavNode[]; account: Account | null }) {
  const p = new URLSearchParams(qs);
  const q = (p.get("q") ?? "").trim();
  const inDept = p.get("in") ?? "";
  /** `log=1`: this is a SUBMITTED search (Enter / View all), counted for the deal picker — the header's predictive keystrokes never send it. */
  const { data, busy } = useLoad<SearchResponse>(q.length >= 2 ? `/shop/search?q=${encodeURIComponent(q)}${inDept ? `&in=${encodeURIComponent(inDept)}` : ""}&limit=200&log=1` : null);
  useEffect(() => { if (q.length >= 2) track("search", { query: q }); }, [q]);
  useEffect(() => { setSeo(q ? `Search: ${q}` : "Search", `Search results for ${q} at Z S MERCH.`, q ? `?q=${encodeURIComponent(q)}` : ""); }, [q]);
  if (q.length < 2) {
    return <div className="empty"><h1>Search the shop</h1><p>Type at least two letters in the search box above.</p></div>;
  }
  if (busy) return <ListSkeleton />;
  if (!data) return <NotFound go={go} what="results page" />;
  return (
    <Listing
      title={`Search results for “${q}”`}
      crumbs={[{ label: "Home", to: { name: "home" } }, { label: "Search" }]}
      products={data.products}
      facets={facetsFromCards(data.products)}
      priceRange={null}
      qs={qs}
      mk={(next) => ({ name: "search", qs: next })}
      go={go}
      trade={account?.list === "wholesale"}
      tree={nav}
      currentId={null}
      sideExtra={data.categories.length > 0 ? (
        <nav className="side-block tree" aria-label="Matching departments">
          <h2>Matching departments</h2>
          <ul>{data.categories.map((c) => <li key={c.id}><Go to={deptRoute(c)} go={go}>{c.name} <span className="n">({c.productCount})</span></Go></li>)}</ul>
        </nav>
      ) : undefined}
      emptyHint={data.didYouMean ? (
        <>No products match “{q}”. Did you mean <Go to={{ name: "search", qs: new URLSearchParams({ q: data.didYouMean, ...(inDept ? { in: inDept } : {}) }).toString() }} go={go}>{data.didYouMean}</Go>?</>
      ) : <>No products match “{q}”. Check the spelling or browse the departments.</>}
    />
  );
}

export function CollectionsPage({ categories, go }: { categories: CategorySummary[]; go: GoFn }) {
  useEffect(() => { setSeo("All collections", "Every Z S MERCH collection."); }, []);
  return (
    <div className="wrap page-pad">
      <h1>All collections</h1>
      <div className="tiles">
        {categories.map((c) => (
          <Go key={c.handle} to={{ name: "category", handle: c.handle }} go={go} className="tile">
            <div className="tile-img">{c.image ? <Pic src={c.image} alt={c.title} sizes="(min-width: 900px) 22vw, 46vw" /> : <div className="skel fill" />}</div>
            <div className="tile-body"><h2>{c.title}</h2><span>{c.productCount} {c.productCount === 1 ? "item" : "items"}</span></div>
          </Go>
        ))}
      </div>
    </div>
  );
}
